/**
 * Validate and sanitize redirect paths to prevent open redirect attacks.
 *
 * Only allows relative paths within the application (same-origin).
 * Rejects absolute URLs, protocol-relative URLs, and paths with encoded characters
 * that could be interpreted as absolute URLs.
 */

/** Set of known application route prefixes */
const ALLOWED_ROUTE_PREFIXES = ['/', '/projects', '/devices'];

/**
 * Validate that a redirect path is safe (relative, same-origin).
 * Returns the sanitized path or '/' if the path is invalid.
 */
export function validateRedirectPath(path: string | null | undefined): string {
  if (!path || typeof path !== 'string') {
    return '/';
  }

  // Trim whitespace
  const trimmed = path.trim();

  // Must start with a single forward slash (not //)
  if (!trimmed.startsWith('/') || trimmed.startsWith('//')) {
    return '/';
  }

  // Reject any path containing protocol indicators
  if (trimmed.includes(':') || trimmed.includes('\\')) {
    return '/';
  }

  // Reject encoded characters that could bypass validation
  // (%2F = /, %5C = \, %3A = :)
  const lower = trimmed.toLowerCase();
  if (lower.includes('%2f') || lower.includes('%5c') || lower.includes('%3a')) {
    return '/';
  }

  // Reject paths with null bytes
  if (trimmed.includes('\0')) {
    return '/';
  }

  // Verify path matches known route structure
  // Allow root path
  if (trimmed === '/') {
    return '/';
  }

  // Check against known route prefixes
  const matchesKnownRoute = ALLOWED_ROUTE_PREFIXES.some(prefix =>
    prefix === '/' ? trimmed === '/' : trimmed.startsWith(prefix)
  );

  if (!matchesKnownRoute) {
    console.warn(`[redirectValidation] Rejected unknown route: ${trimmed}`);
    return '/';
  }

  return trimmed;
}
